// Чистая логика редактора аудиторий (общая для кампаний, шаблонов, bulk-update и AB-тестов):
// какой контрол показать для строки параметра, какие опции и ошибки у него есть.
import {POSSIBLE_OPERATORS} from "../Campaign/campaignFormModel";

export const OFFERS_PARAMETERS = ['offers_in_profile', 'offers_not_in_profile'];
export const OFFSET_PARAMETERS = ['profile_offset_from_end', 'profile_offset_from_start'];
// значения из справочника, даже если allowedValues пустой
export const DICTIONARY_PARAMETERS = ['ab_test_cohort'];
// можно вводить свои значения помимо справочника
export const FREE_SOLO_VALUE_PARAMETERS = ['type_inactive', 'attribution_campaign', 'tag_inactive', 'tag_not_in_profile', 'tags_in_profile'];
// операторы со списком значений
export const MULTI_VALUE_OPERATORS = [
    'includeAny', 'excludeAll', 'excludeAny', 'includeAll', 'offerTagInactiveRule', 'offerTagNotInProfileRule', 'offerTagsInProfileRule',
];

/**
 * Различия кампаний и AB-тестов (сохранены как были в двух отдельных AudienceStep).
 * Ширины колонок — в единицах Grid (из 12).
 */
export const AUDIENCE_VARIANTS = {
    campaign: {
        minWidth: 800,
        // у этих параметров оператор единственный и колонка оператора скрыта
        hideOperatorFor: ['type_inactive', 'offer_inactive'],
        freeSoloOperatorFor: [],
        singleValueFor: [],
        filterCohortsByAbTest: false,
        operatorWidth: 2.5,
        valueWidth: 5.5,
        offsetWidths: [4, 2, 6],
        fickleWarning: true,
        cloneButtonColor: 'info',
    },
    abtest: {
        minWidth: 800,
        hideOperatorFor: [],
        freeSoloOperatorFor: ['offer_inactive', 'type_inactive', 'tag_inactive', 'tag_not_in_profile', 'tags_in_profile'],
        singleValueFor: ['ab_test'],
        filterCohortsByAbTest: true,
        operatorWidth: 2,
        valueWidth: 6,
        offsetWidths: [5, 2, 5],
        fickleWarning: false,
        cloneButtonColor: 'warning',
    },
};

/** Индекс доступных параметров по имени (вместо filter() на каждый рендер каждой строки). */
export const indexParameters = (params) => new Map((params || []).map(p => [p.parameterName, p]));

export const operatorsFor = (definition) => definition
    ? definition.allowedRules.flatMap(rule => POSSIBLE_OPERATORS.get(rule)).filter(Boolean)
    : [];

const isRange = (parameterSet) => parameterSet.hasOwnProperty('fromIncl') || parameterSet.hasOwnProperty('toIncl');

/**
 * Описание строки параметра: какие колонки и контролы показывать.
 * kind значения: 'range' | 'multi' | 'offset' | 'text'.
 */
export const describeParameter = (parameterSet, paramsIndex, variant = AUDIENCE_VARIANTS.campaign) => {
    const name = parameterSet?.parameterData?.parameterName || '';
    const definition = paramsIndex.get(name);
    const allowedValues = definition?.allowedValues || [];
    const isOffers = OFFERS_PARAMETERS.includes(name);
    const isOffset = OFFSET_PARAMETERS.includes(name);
    const range = isRange(parameterSet);

    let valueKind = 'text';
    if (range) valueKind = 'range';
    else if (MULTI_VALUE_OPERATORS.includes(parameterSet.operator)
        || (!isOffset && DICTIONARY_PARAMETERS.includes(name))
        || allowedValues.length !== 0) valueKind = 'multi';
    else if (isOffset) valueKind = 'offset';

    const showOperatorColumn = !isOffers && !isOffset && !variant.hideOperatorFor.includes(name);
    return {
        name,
        definition,
        valueKind,
        showOperatorColumn,
        // для диапазона колонка оператора занята полями от/до
        operatorColumnWidth: range ? 8 : variant.operatorWidth,
        valueColumnWidth: isOffers || isOffset ? 8 : variant.valueWidth,
        operatorOptions: operatorsFor(definition),
        operatorFreeSolo: variant.freeSoloOperatorFor.includes(name),
        valueOptions: allowedValues,
        valueFreeSolo: FREE_SOLO_VALUE_PARAMETERS.includes(name),
        valueMultiple: !variant.singleValueFor.includes(name),
    };
};

/** В AB-тестах после выбора когорты остальные варианты ограничены тем же AB-тестом. */
export const filterCohortOptions = (options, paramValue) => {
    const first = Array.isArray(paramValue) ? paramValue[0] : null;
    if (!first) return options;
    const abTest = first.split(':')[0];
    return options.filter(option => option.split(':')[0] === abTest);
};

/** Значение, которое Autocomplete ожидает для multi-поля. */
export const multiValue = (paramValue, multiple) => {
    if (!multiple) return paramValue ? paramValue : null;
    return paramValue ? paramValue : [];
};

/** Добавление набранного текста по blur (как раньше: без дублей, с trim). */
export const appendTypedValue = (paramValue, typed) => {
    const value = typed.trim();
    const current = paramValue || [];
    if (!value || current.includes(value)) return null;
    return [...current, value];
};

/** Ошибки диапазонов: поддерживаются Set и массивы, записи {audience, parameterName, paramIndex?}. */
export const hasRangeError = (errorFields, audienceIndex, paramIndex, parameterName) => [...(errorFields || [])].some(entry =>
    entry && typeof entry === 'object'
    && entry.audience === audienceIndex
    && entry.parameterName === parameterName
    && (entry.paramIndex === undefined || entry.paramIndex === paramIndex)
);

/** Поле выбора параметра подсвечивается, если строка не заполнена и в ней есть ошибки. */
export const requirementHasError = (parameterSet) => !!parameterSet.paramNameError
    || (!parameterSet?.parameterData?.parameterName && !!(parameterSet.valueError || parameterSet.operatorError));

/** Индекс выбранной аудитории после удаления аудитории index. */
export const indexAfterDelete = (current, deleted) => {
    if (deleted > current) return current;
    return current > 1 ? current - 1 : 0;
};
